import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/icons';
import { Badge, EmptyState, ErrorState, Field, LoadingRows, Modal, PageHeader } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errors';
import { formatDate, includesTr } from '../lib/format';
import { qk, useFaqs } from '../lib/queries';
import type { Faq } from '../lib/types';

interface Draft {
  id?: string;
  question: string;
  answer: string;
  category: string;
  tags: string;
}

const EMPTY_DRAFT: Draft = { question: '', answer: '', category: 'Genel', tags: '' };

/** S7 — SSS: tablo + editör (soru/cevap/etiket/kategori) + önizleme. */
export default function FaqPage() {
  const faqs = useFaqs();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [draft, setDraft] = useState<Draft | null>(null);

  const categories = useMemo(() => {
    const set = new Set((faqs.data?.items ?? []).map((faq) => faq.category));
    return ['all', ...Array.from(set)];
  }, [faqs.data]);

  const rows = useMemo(() => {
    const items = faqs.data?.items ?? [];
    return items.filter(
      (faq) =>
        (category === 'all' || faq.category === category) &&
        (includesTr(faq.question, search) || includesTr(faq.answer, search) || faq.tags.some((tag) => includesTr(tag, search)))
    );
  }, [faqs.data, category, search]);

  const save = useMutation({
    mutationFn: (value: Draft) =>
      api.saveFaq({
        id: value.id,
        question: value.question.trim(),
        answer: value.answer.trim(),
        category: value.category.trim() || 'Genel',
        tags: value.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean)
      }),
    onSuccess: () => {
      toast.push('SSS kaydedildi; indeksleme kuyruğa alındı.', 'ok');
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: qk.faqs });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const toggle = useMutation({
    mutationFn: (faq: Faq) => api.toggleFaq(faq.id, !faq.active),
    onSuccess: (faq) => {
      toast.push(faq.active ? 'SSS aktifleştirildi.' : 'SSS pasife alındı; AI artık kullanmaz.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.faqs });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteFaq(id),
    onSuccess: () => {
      toast.push('SSS silindi.', 'ok');
      void queryClient.invalidateQueries({ queryKey: qk.faqs });
    },
    onError: (error) => toast.push(errorMessage(error), 'error')
  });

  return (
    <div>
      <PageHeader
        title="SSS"
        subtitle="AI'nın doğrudan kullandığı bilgi tabanı. Pasif kayıtlar yanıtlarda kullanılmaz."
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setDraft({ ...EMPTY_DRAFT })}>
            <Icon name="plus" size={14} />
            Yeni SSS
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-6 py-2">
        <div className="relative w-[320px]">
          <Icon name="search" size={14} className="pointer-events-none absolute left-2 top-2 text-muted" />
          <input
            className="input pl-7"
            placeholder="Soru, cevap veya etiket ara"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="SSS ara"
          />
        </div>
        <select className="select w-[180px]" value={category} onChange={(event) => setCategory(event.target.value)} aria-label="Kategori">
          {categories.map((item) => (
            <option key={item} value={item}>
              {item === 'all' ? 'Tüm kategoriler' : item}
            </option>
          ))}
        </select>
        <span className="ml-auto text-[11px] text-muted">{rows.length} kayıt</span>
      </div>

      {faqs.isError ? (
        <ErrorState message={errorMessage(faqs.error)} onRetry={() => void faqs.refetch()} />
      ) : faqs.isLoading ? (
        <LoadingRows rows={8} columns={4} />
      ) : rows.length === 0 ? (
        <EmptyState icon="help" title="SSS kaydı yok" hint="Yeni SSS ekleyin veya arama ölçütlerini değiştirin." />
      ) : (
        <table className="table">
          <caption className="sr-only">SSS listesi</caption>
          <thead>
            <tr>
              <th scope="col">Soru</th>
              <th scope="col" style={{ width: '140px' }}>Kategori</th>
              <th scope="col" style={{ width: '120px' }}>Etiketler</th>
              <th scope="col" style={{ width: '90px' }} className="text-right">Kullanım</th>
              <th scope="col" style={{ width: '120px' }}>Güncelleme</th>
              <th scope="col" style={{ width: '140px' }}>Durum</th>
              <th scope="col" style={{ width: '120px' }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((faq) => (
              <tr key={faq.id}>
                <td className="max-w-[420px]">
                  <button type="button" className="truncate text-left hover:underline" onClick={() => setDraft(toDraft(faq))}>
                    {faq.question}
                  </button>
                </td>
                <td>
                  <Badge>{faq.category}</Badge>
                </td>
                <td className="text-[12px] text-muted">{faq.tags.join(', ') || '—'}</td>
                <td className="mono text-right text-[12px]">{faq.hits}</td>
                <td className="mono text-[12px] text-muted">{formatDate(faq.updatedAt)}</td>
                <td>
                  <button
                    type="button"
                    className={'badge ' + (faq.active ? 'badge-ok' : '')}
                    onClick={() => toggle.mutate(faq)}
                    aria-pressed={faq.active}
                  >
                    {faq.active ? 'aktif' : 'pasif'}
                  </button>
                </td>
                <td className="text-right">
                  <div className="flex justify-end gap-1">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(toDraft(faq))} aria-label="Düzenle">
                      <Icon name="gear" size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm text-danger"
                      aria-label="Sil"
                      onClick={() => {
                        if (window.confirm('"' + faq.question + '" silinsin mi?')) remove.mutate(faq.id);
                      }}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        open={draft !== null}
        title={draft?.id ? 'SSS düzenle' : 'Yeni SSS'}
        wide
        onClose={() => setDraft(null)}
        footer={
          <>
            <button type="button" className="btn btn-sm" onClick={() => setDraft(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draft?.question.trim() || !draft?.answer.trim() || save.isPending}
              onClick={() => draft && save.mutate(draft)}
            >
              Kaydet
            </button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-3">
            <Field label="Soru" htmlFor="faq-question">
              <input
                id="faq-question"
                className="input"
                value={draft.question}
                onChange={(event) => setDraft({ ...draft, question: event.target.value })}
                placeholder="Kargom ne zaman gelir?"
              />
            </Field>
            <Field label="Cevap" htmlFor="faq-answer" hint="AI bu metni doğrudan kullanır; kısa ve net yazın.">
              <textarea
                id="faq-answer"
                className="textarea min-h-[120px]"
                value={draft.answer}
                onChange={(event) => setDraft({ ...draft, answer: event.target.value })}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Kategori" htmlFor="faq-category">
                <input
                  id="faq-category"
                  className="input"
                  value={draft.category}
                  onChange={(event) => setDraft({ ...draft, category: event.target.value })}
                  list="faq-categories"
                />
                <datalist id="faq-categories">
                  {categories
                    .filter((item) => item !== 'all')
                    .map((item) => (
                      <option key={item} value={item} />
                    ))}
                </datalist>
              </Field>
              <Field label="Etiketler" htmlFor="faq-tags" hint="Virgülle ayırın">
                <input
                  id="faq-tags"
                  className="input"
                  value={draft.tags}
                  onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
                  placeholder="kargo, teslimat"
                />
              </Field>
            </div>

            <div>
              <div className="eyebrow mb-1">Önizleme — AI cevabı</div>
              <div className="rounded-[4px] border border-line bg-paper p-3 text-[13px]">
                <div className="text-[11px] text-muted">Müşteri sorar:</div>
                <div className="font-medium">{draft.question || 'Soru yazın'}</div>
                <div className="mt-2 text-[11px] text-muted">AI yanıtı:</div>
                <div className="whitespace-pre-wrap">{draft.answer || 'Cevap yazın'}</div>
                <div className="mt-2 text-[11px] text-muted">Kaynak: SSS · {draft.category || 'Genel'}</div>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function toDraft(faq: Faq): Draft {
  return {
    id: faq.id,
    question: faq.question,
    answer: faq.answer,
    category: faq.category,
    tags: faq.tags.join(', ')
  };
}
